import { AlertTriangle, BatteryFull, Gauge, PlugZap, WifiLow, WifiOff } from 'lucide-react';
import {
  hasPermission,
  INSTALLER_EVENTS,
  NOTIFICATION_EVENTS,
  renderNotification,
  type NotificationEvent,
  type SiteEvent,
} from '@ste/shared';
import { useConnection, useCurrentSite } from '../../api/connection.ts';
import {
  useEvents,
  useNotificationCommands,
  useNotificationSettings,
  usePushStatus,
} from '../../api/notifications.ts';
import { useBaseRole, useRole } from '../../api/role.ts';
import { Banner, Button, Card, EmptyState, PageHeader, Spinner, Toggle } from '../../components/ui.tsx';
import { t, type MessageKey } from '../../i18n/index.ts';
import { isNativeApp } from '../../native/platform.ts';

const ICONS: Record<NotificationEvent, typeof PlugZap> = {
  'charge.finished': PlugZap,
  'vehicle.target_reached': BatteryFull,
  'device.fault': AlertTriangle,
  'gateway.offline': WifiOff,
  'grid.dimmed': Gauge,
  'network.wifi_weak': WifiLow,
};

const timeFormat = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

export function NotificationsPage() {
  const mode = useConnection((s) => s.mode);
  return (
    <div className="space-y-6">
      <PageHeader title={t('notifications.title')} />
      {mode === 'cloud' ? <PushSettings /> : <Banner tone="muted" title={t('notifications.gatewayOnly')} />}
      <EventLog />
    </div>
  );
}

function PushSettings() {
  const settings = useNotificationSettings();
  const push = usePushStatus();
  const { save, test } = useNotificationCommands();
  const role = useRole();
  const baseRole = useBaseRole();
  const site = useCurrentSite();
  // installer-only events (e.g. weak WLAN) are not offered to customers
  const staff = baseRole === 'installer' || baseRole === 'admin';
  const offered = NOTIFICATION_EVENTS.filter((e) => staff || !INSTALLER_EVENTS.includes(e));
  const enabled = new Set(settings.data?.events ?? []);
  const devices = push.data?.devices ?? 0;

  const toggle = (event: NotificationEvent, on: boolean) => {
    const next = NOTIFICATION_EVENTS.filter((e) => (e === event ? on : enabled.has(e)));
    save.mutate(next);
  };

  return (
    <Card title={t('notifications.push')}>
      <div className="space-y-5">
        <p className="text-muted">
          {isNativeApp() ? t('notifications.appHint') : t('notifications.browserHint')}
          {site && ` ${t('notifications.forSite', { site: site.title })}`}
        </p>
        {push.data && !push.data.enabled && role !== undefined && hasPermission(role, 'site:configure') && (
          <Banner tone="warm" title={t('notifications.serverOff')} />
        )}
        {settings.isPending ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <div className="space-y-5">
            {offered.map((event) => (
              <Toggle
                key={event}
                label={t(`notifications.event.${event}` as MessageKey)}
                hint={t(`notifications.hint.${event}` as MessageKey)}
                checked={enabled.has(event)}
                onChange={(on) => toggle(event, on)}
              />
            ))}
          </div>
        )}
        {save.isError && <Banner tone="danger" title={t('notifications.saveFailed')} />}
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
          <Button
            variant="secondary"
            onClick={() => test.mutate()}
            disabled={devices === 0 || test.isPending}
          >
            {t('notifications.test')}
          </Button>
          <span className="text-sm text-muted" aria-live="polite">
            {test.data
              ? test.data.sent > 0
                ? t('notifications.testSent', { n: test.data.sent })
                : t('notifications.testFailed')
              : devices === 0
                ? t('notifications.noDevices')
                : t('notifications.devices', { n: devices })}
          </span>
        </div>
      </div>
    </Card>
  );
}

function EventLog() {
  const events = useEvents();
  return (
    <Card title={t('notifications.log')}>
      {events.isPending ? (
        <Spinner label={t('common.loading')} />
      ) : events.isError ? (
        <Banner tone="warm" title={t('notifications.logUnavailable')} />
      ) : events.data.events.length === 0 ? (
        <EmptyState>{t('notifications.logEmpty')}</EmptyState>
      ) : (
        <ul className="divide-y divide-border">
          {events.data.events.map((event) => (
            <EventRow key={event.id} event={event} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function EventRow({ event }: { event: SiteEvent }) {
  const Icon = ICONS[event.type];
  const text = renderNotification(event);
  const warn = event.type === 'device.fault' || event.type === 'gateway.offline';
  return (
    <li className="flex gap-3 py-3">
      <Icon
        size={20}
        className={warn ? 'mt-0.5 shrink-0 text-warm-text' : 'mt-0.5 shrink-0 text-accent-text'}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="font-medium">{text.title}</span>
          <time className="text-sm text-muted tabular-nums" dateTime={event.ts}>
            {timeFormat.format(new Date(event.ts))}
          </time>
        </div>
        <p className="text-sm text-muted">{text.body}</p>
      </div>
    </li>
  );
}
